import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QuoteDetailDto, UpdateQuoteRequest } from "@/lib/quotes";
import { quotesApi } from "@/lib/quotes";
import { ClientAddressPicker } from "@/components/client-address-picker";
import { ClientContactPicker } from "@/components/client-contact-picker";
import { PaymentTermsPicker } from "@/components/payment-terms-picker";
import { DeliveryMethodPicker } from "@/components/delivery-method-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { Link } from "react-router";
import { Pencil, X, Save } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

export interface QuoteCustomerDetailsPanelProps {
  quote: QuoteDetailDto;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-sm font-bold text-[#1a2f4c] mb-0.5">{label}</dt>
      <dd className="whitespace-pre-line break-words text-sm text-slate-700">
        {children}
      </dd>
    </div>
  );
}

export function QuoteCustomerDetailsPanel({ quote }: QuoteCustomerDetailsPanelProps) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  // Edit-mode state mirrors the quote, then diverges as the user types
  const [client, setClient] = useState<ClientPickerValue | null>(
    quote.clientId 
      ? { 
          clientId: quote.clientId, 
          fullName: quote.clientName ?? "", 
          emailAddress: null, 
          companyId: null, 
          companyName: null 
        } 
      : null
  );
  const [billingAddressId, setBillingAddressId] = useState<number | null>(quote.billingAddressId ?? null);
  const [deliveryAddressId, setDeliveryAddressId] = useState<number | null>(quote.deliveryAddressId ?? null);
  const [contactId, setContactId] = useState<number | null>(quote.contactId ?? null);
  const [paymentTermsId, setPaymentTermsId] = useState<number | null>(quote.paymentTermsId ?? null);
  const [deliveryMethodId, setDeliveryMethodId] = useState<number | null>(quote.deliveryMethodId ?? null);
  const [purchaseOrder, setPurchaseOrder] = useState<string>(quote.purchaseOrder ?? "");

  const update = useMutation({
    mutationFn: (body: UpdateQuoteRequest) => quotesApi.update(quote.quoteId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", quote.quoteId] });
      setEditing(false);
    },
  });

  const sameAddress =
    quote.billingAddressId != null &&
    quote.deliveryAddressId != null &&
    quote.billingAddressId === quote.deliveryAddressId;

  function onSave() {
    update.mutate({
      // existing required fields — leave the others alone by passing the loaded values
      clientId: client?.clientId ?? null,
      ownerEmployeeId: quote.ownerEmployeeId,
      branchId: quote.branchId,
      description: quote.description,
      approvalTypeCode: quote.approvalTypeCode,
      quoteTemplateId: quote.quoteTemplateId,
      validUntil: quote.validUntil,
      rowVersionBase64: quote.rowVersionBase64 ?? "",
      // Customer details
      billingAddressId,
      deliveryAddressId,
      contactId,
      paymentTermsId,
      deliveryMethodId,
      purchaseOrder: purchaseOrder.trim() === "" ? null : purchaseOrder,
    });
  }

  function onCancel() {
    // Reset state to the loaded quote, then exit edit mode
    setClient(
      quote.clientId 
        ? { 
            clientId: quote.clientId, 
            fullName: quote.clientName ?? "", 
            emailAddress: null, 
            companyId: null, 
            companyName: null 
          } 
        : null
    );
    setBillingAddressId(quote.billingAddressId ?? null);
    setDeliveryAddressId(quote.deliveryAddressId ?? null);
    setContactId(quote.contactId ?? null);
    setPaymentTermsId(quote.paymentTermsId ?? null);
    setDeliveryMethodId(quote.deliveryMethodId ?? null);
    setPurchaseOrder(quote.purchaseOrder ?? "");
    setEditing(false);
  }

  return (
    <Card className="border border-slate-400 rounded-xl overflow-hidden bg-white shadow-sm flex-1 flex flex-col">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4 flex flex-row items-center justify-between space-y-0">
        <CardTitle role="heading" aria-level={3} className="text-xs font-bold text-slate-800 uppercase tracking-wider">Customer Details</CardTitle>
        {!editing && (
          <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-slate-700" onClick={() => setEditing(true)}>
            <Pencil className="h-3.5 w-3.5" />
            <span className="sr-only">Edit Details</span>
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-4 flex flex-col flex-1">
        {!editing ? (
          <dl className="space-y-3">
            <Row label="Customer">
              {quote.clientId != null ? (
                <Link
                  to={`/clients/${quote.clientId}`}
                  className="text-primary underline hover:opacity-80"
                >
                  {quote.clientName ?? "—"}
                </Link>
              ) : (
                quote.clientName ?? "—"
              )}
            </Row>
            <Row label="Contact">{quote.contactDisplayName ?? "—"}</Row>
            <Row label="Contact Email">
              {quote.contactEmail ? (
                <a href={`mailto:${quote.contactEmail}`} className="text-primary hover:underline">
                  {quote.contactEmail}
                </a>
              ) : (
                "—"
              )}
            </Row>
            <Row label="Contact Phone">{quote.contactPhone ?? "—"}</Row>
            <Row label="Billing Address">{quote.billingAddressFormatted ?? "—"}</Row>
            <Row label="Delivery Address">
              {sameAddress ? "Same as Billing Address" : (quote.deliveryAddressFormatted ?? "—")}
            </Row>
            
            <div className="flex flex-col gap-3 pt-2">
              <div className="flex items-center gap-2">
                <Checkbox id="same-billing" checked={sameAddress} className="border-slate-400 rounded-sm" />
                <Label htmlFor="same-billing" className="text-sm text-slate-600 font-normal">Same as Billing Address</Label>
              </div>
            </div>


          </dl>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-[130px_1fr] items-center gap-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground text-right">Customer</Label>
              <div className="min-w-0">
                <ClientPicker 
                  value={client} 
                  onChange={(newClient) => {
                    setClient(newClient);
                    if (newClient?.clientId !== client?.clientId) {
                      setContactId(null);
                      setBillingAddressId(null);
                      setDeliveryAddressId(null);
                    }
                  }} 
                />
              </div>
            </div>
            <div className="grid grid-cols-[130px_1fr] items-center gap-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground text-right">Contact</Label>
              <div className="min-w-0">
                <ClientContactPicker clientId={client?.clientId ?? null} value={contactId} onChange={setContactId} />
              </div>
            </div>
            <div className="grid grid-cols-[130px_1fr] items-center gap-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground text-right">Billing Address</Label>
              <div className="min-w-0">
                <ClientAddressPicker clientId={client?.clientId ?? null} value={billingAddressId} onChange={setBillingAddressId} />
              </div>
            </div>
            <div className="grid grid-cols-[130px_1fr] items-center gap-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground text-right">Delivery Address</Label>
              <div className="min-w-0">
                <ClientAddressPicker clientId={client?.clientId ?? null} value={deliveryAddressId} onChange={setDeliveryAddressId} />
              </div>
            </div>
            <div className="grid grid-cols-[130px_1fr] items-center gap-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground text-right">Payment Terms</Label>
              <div className="min-w-0">
                <PaymentTermsPicker value={paymentTermsId} onChange={setPaymentTermsId} />
              </div>
            </div>
            <div className="grid grid-cols-[130px_1fr] items-center gap-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground text-right">Delivery Method</Label>
              <div className="min-w-0">
                <DeliveryMethodPicker value={deliveryMethodId} onChange={setDeliveryMethodId} />
              </div>
            </div>
            <div className="grid grid-cols-[130px_1fr] items-center gap-4">
              <Label
                htmlFor="purchaseOrder"
                className="text-xs uppercase tracking-wide text-muted-foreground text-right"
              >
                Purchase Order
              </Label>
              <div className="min-w-0">
                <Input
                  id="purchaseOrder"
                  value={purchaseOrder}
                  onChange={(e) => setPurchaseOrder(e.target.value)}
                />
              </div>
            </div>
            
            <div className="pt-4 mt-auto flex flex-wrap justify-end gap-2">
              <Button variant="outline" className="rounded-full border-transparent bg-info/15 text-primary hover:bg-info/25 font-semibold w-full sm:w-auto" size="sm" onClick={onCancel}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button className="rounded-full border-transparent bg-[#0f3453] text-white hover:bg-[#1a2f4c] font-semibold w-full sm:w-auto" size="sm" onClick={onSave} disabled={update.isPending}>
                <Save className="w-4 h-4 mr-2" />
                {update.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
